import { GenericObjectType } from "@rjsf/utils";

import { DerivedRule } from "./flatten-derived-rules";

/** Get value from nested path (e.g., "outputs.studyArea.id") */
export const getNestedValue = (obj: GenericObjectType, path: string): unknown => {
  const keys = path.split(".");
  let current: unknown = obj;
  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = (current as GenericObjectType)[key];
    } else {
      return undefined;
    }
  }
  return current;
};

/** Set value at nested path immutably */
const setNestedValue = <T extends GenericObjectType>(obj: T, path: string, value: unknown): T => {
  const keys = path.split(".");
  if (keys.length === 1) return { ...obj, [path]: value };

  const [first, ...rest] = keys;
  return {
    ...obj,
    [first]: setNestedValue((obj[first] || {}) as GenericObjectType, rest.join("."), value),
  };
};

type ApplyDerivedRulesResult<T extends GenericObjectType> = {
  updatedValues: T;
  updatedRules: Record<string, DerivedRule<T>>;
};

/**
 * Apply all active derivation rules to form data.
 *
 * Deactivation strategy: a rule is deactivated when the current field value diverges from
 * `syncedValue` (the last value the rule wrote, or the initial form value). This catches edits
 * made in any prior cycle, including before the rule has ever fired.
 */
export const applyDerivedRules = <T extends GenericObjectType>(
  values: T,
  rules: Record<string, DerivedRule<T>>
): ApplyDerivedRulesResult<T> => {
  let result = { ...values };
  const updatedRules = { ...rules };

  Object.entries(rules).forEach(([path, rule]) => {
    if (!rule.isActive) return;

    const currentValue = getNestedValue(values, path);
    const expectedValue = rule.getValue(values);

    // When the rule has nothing to return but was previously in control of this field
    // (field is still at the last value the rule wrote), reset to the initial value.
    if (expectedValue === undefined) {
      if (currentValue === rule.syncedValue) {
        updatedRules[path] = { ...rule, syncedValue: rule.initialValue };
        result = setNestedValue(result, path, rule.initialValue);
      }
      return;
    }

    // Deactivate when the user has manually edited this field.
    // syncedValue tracks the last value written by the rule (or the initial form value),
    // so divergence is detected across cycles, not just within the current update.
    const wasManuallyEdited = rule.syncedValue !== undefined && currentValue !== rule.syncedValue;

    if (wasManuallyEdited && currentValue !== expectedValue) {
      updatedRules[path] = { ...rule, isActive: false };
      return;
    }

    // Apply the rule and keep syncedValue in sync so future edits are detected correctly.
    updatedRules[path] = { ...rule, syncedValue: expectedValue };
    result = setNestedValue(result, path, expectedValue);
  });

  return { updatedValues: result, updatedRules };
};
